import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, userHasMfa } from '@/contexts/AuthContext';
import { useOrg } from '@/contexts/OrgContext';
import { MfaEnrollment } from '@/components/MfaEnrollment';
import { getFirestoreDb } from '@/lib/firebase';

export function MfaPolicyGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { orgId, skipLiveUpdate } = useOrg();
  const [mfaPolicy, setMfaPolicy] = useState<'optional' | 'required'>('optional');
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    if (!user) return;
    setEnrolled(userHasMfa(user));
  }, [user]);

  useEffect(() => {
    if (!orgId || !user) return;
    return onSnapshot(doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'security'), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      const p = snap.data()?.mfaPolicy;
      setMfaPolicy(p === 'required' ? 'required' : 'optional');
    });
  }, [orgId, user, skipLiveUpdate]);

  if (mfaPolicy === 'required' && user && !enrolled) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--fv-bg)] p-6">
        <div className="w-full max-w-md rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-8">
          <h1 className="text-xl font-bold">{t('auth.mfaRequiredTitle')}</h1>
          <p className="mt-2 text-sm text-[var(--fv-text-muted)]">{t('auth.mfaRequiredBody')}</p>
          <MfaEnrollment user={user} onEnrolled={() => setEnrolled(true)} />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
